const Autodesk = window.Autodesk;
const THREE = window.THREE;

import store from '@/store';

/**
 * PivotSetterTool - Sets navigation pivot on middle-button double-click
 * and prevents pivot reset to origin on left double-click empty space.
 *
 * Uses Forge ToolInterface at priority 100 to intercept events
 * before the default navigation tool (priority 0) can handle them.
 *
 * - Left double-click on empty space: consumed (prevents pivot reset to origin)
 * - Left double-click on geometry: passes through (normal behavior)
 * - First middle-click: records timestamp, allows pan (returns false)
 * - Second middle-click within 600ms: sets pivot, blocks pan (returns true)
 * - Ctrl + Left click: sets pivot immediately (visual feedback shown)
 * - Non-middle clicks: passes through (returns false)
 */
class PivotSetterTool extends Autodesk.Viewing.ToolInterface {
  constructor(viewer) {
    super();
    this.viewer = viewer;
    this.names = ['pivot-setter-tool'];
    this.lastMiddleClickTime = 0;
    this.tempGizmo = null;
    this.gizmoTimeout = null;

    // Delete instance methods so toolController calls class methods
    // Only delete methods that have class-level replacements below
    delete this.getPriority;
    delete this.handleButtonDown;
    delete this.handleDoubleClick;
    delete this.handleSingleClick;
  }

  getNames() {
    return this.names;
  }

  getPriority() {
    return 100; // Higher than pan tool (0) — intercepts events first
  }

  handleDoubleClick(event, button) {
    if (button !== 0) return false; // Only intercept left button

    // Hit-test: is there geometry under the cursor?
    let mainExtension = store.state.forgeService.getMainExtension();
    let pointOnPointCloud = mainExtension
      ? mainExtension.pickNearestPointCloudPoint(event)
      : null;
    let hitTest = this.viewer.clientToWorld(event.offsetX, event.offsetY, false);
    let pointOnModel = hitTest && hitTest.point ? hitTest.point : null;

    if (!pointOnPointCloud && !pointOnModel) {
      // Empty space — consume event to prevent Forge resetting pivot to origin
      return true;
    }

    // Geometry was hit — let Forge handle normally
    return false;
  }

  handleSingleClick(event, button) {
    // Ctrl + Left click to set pivot immediately
    if (button === 0 && event.ctrlKey) {
      this._setPivotFromEvent(event, true);
      return true; // Consume event
    }
    return false;
  }

  handleButtonDown(event, button) {
    if (button !== 1) return false; // Only middle button

    let now = Date.now();
    if (now - this.lastMiddleClickTime < 600) {
      // Second click — set pivot, block pan
      this.lastMiddleClickTime = 0;
      this._setPivotFromEvent(event, true);
      return true; // Consume event — prevents pan tool from starting
    } else {
      // First click — record time, allow pan
      this.lastMiddleClickTime = now;
      return false;
    }
  }

  /**
   * Hit-tests both point cloud and model, picks whichever is closer to camera.
   * @param {Object} event - Mouse event with offsetX, offsetY
   * @param {boolean} showGizmo - Whether to show visual feedback gizmo
   */
  _setPivotFromEvent(event, showGizmo = false) {
    let mainExtension = store.state.forgeService.getMainExtension();
    let pointOnPointCloud = mainExtension
      ? mainExtension.pickNearestPointCloudPoint(event)
      : null;

    let hitTest = this.viewer.clientToWorld(event.offsetX, event.offsetY, false);
    let pointOnModel = hitTest && hitTest.point ? hitTest.point : null;

    let point = null;
    if (pointOnModel && pointOnPointCloud) {
      let cameraPt = this.viewer.navigation.getPosition();
      point = cameraPt.distanceTo(pointOnModel) < cameraPt.distanceTo(pointOnPointCloud)
        ? pointOnModel
        : pointOnPointCloud;
    } else {
      point = pointOnPointCloud || pointOnModel;
    }

    if (point) {
      this.viewer.utilities.setPivotPoint(point, true, true);

      if (showGizmo) {
        this._showPivotGizmo(point);
      }
    }
  }

  /**
   * Shows a temporary pivot gizmo using cylinders and cones.
   * Same approach as crop box arrows - uses depthTest=false, depthWrite=false for always-on-top.
   * Respects model unit scale (meters vs mm).
   * The gizmo auto-hides after 1.5 seconds.
   * @param {THREE.Vector3} point - Position to show gizmo
   */
  _showPivotGizmo(point) {
    // Clear any existing gizmo
    this._hidePivotGizmo();

    // Get unit scale factor (1 for meters, 1000 for mm)
    const unitScale = store.state.unitScale || 1;
    const scaleFactor = 1 / unitScale;

    // Base sizes scaled by unit
    const axisLength = 0.225 * scaleFactor;
    const cylinderRadius = 0.015 * scaleFactor;
    const coneRadius = 0.04 * scaleFactor;
    const coneHeight = 0.06 * scaleFactor;
    const sphereRadius = 0.025 * scaleFactor;
    const gizmoGroup = [];

    // Helper to create cylinder between two points
    const createCylinder = (start, end, color) => {
      const distance = start.distanceTo(end);
      const position = start.clone().add(end).divideScalar(2);

      const material = new THREE.MeshBasicMaterial({ color, depthTest: false, depthWrite: false });
      material.polygonOffset = true;
      material.polygonOffsetFactor = -10;
      material.polygonOffsetUnits = -10;
      const geometry = new THREE.CylinderGeometry(cylinderRadius, cylinderRadius, distance, 12);

      // Orient cylinder
      const direction = end.clone().sub(start).normalize();
      const quaternion = new THREE.Quaternion();
      quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);

      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.copy(position);
      mesh.setRotationFromQuaternion(quaternion);
      mesh.renderOrder = 999999;

      return mesh;
    };

    // Helper to create cone at tip (using CylinderGeometry with topRadius=0 for older THREE.js)
    const createCone = (position, direction, color) => {
      const material = new THREE.MeshBasicMaterial({ color, depthTest: false, depthWrite: false });
      material.polygonOffset = true;
      material.polygonOffsetFactor = -10;
      material.polygonOffsetUnits = -10;
      // CylinderGeometry(topRadius, bottomRadius, height, segments) - topRadius=0 makes a cone
      const geometry = new THREE.CylinderGeometry(0, coneRadius, coneHeight, 12);

      const quaternion = new THREE.Quaternion();
      quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);

      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.copy(position);
      mesh.setRotationFromQuaternion(quaternion);
      mesh.renderOrder = 999999;

      return mesh;
    };

    // X axis (red)
    const xEnd = new THREE.Vector3(point.x + axisLength, point.y, point.z);
    gizmoGroup.push(createCylinder(point, xEnd, 0xff0000));
    gizmoGroup.push(createCone(xEnd, new THREE.Vector3(1, 0, 0), 0xff0000));

    // Y axis (green)
    const yEnd = new THREE.Vector3(point.x, point.y + axisLength, point.z);
    gizmoGroup.push(createCylinder(point, yEnd, 0x00ff00));
    gizmoGroup.push(createCone(yEnd, new THREE.Vector3(0, 1, 0), 0x00ff00));

    // Z axis (blue)
    const zEnd = new THREE.Vector3(point.x, point.y, point.z + axisLength);
    gizmoGroup.push(createCylinder(point, zEnd, 0x0000ff));
    gizmoGroup.push(createCone(zEnd, new THREE.Vector3(0, 0, 1), 0x0000ff));

    // Center sphere at origin (yellow)
    const sphereGeom = new THREE.SphereGeometry(sphereRadius, 12, 12);
    const sphereMat = new THREE.MeshBasicMaterial({ color: 0xffff00, depthTest: false, depthWrite: false });
    sphereMat.polygonOffset = true;
    sphereMat.polygonOffsetFactor = -10;
    sphereMat.polygonOffsetUnits = -10;
    const centerSphere = new THREE.Mesh(sphereGeom, sphereMat);
    centerSphere.position.copy(point);
    centerSphere.renderOrder = 999999;
    gizmoGroup.push(centerSphere);

    // Add to overlay scene
    if (!this.viewer.overlays.hasScene('pivot-gizmo')) {
      this.viewer.overlays.addScene('pivot-gizmo');
    }

    gizmoGroup.forEach(mesh => {
      this.viewer.overlays.addMesh(mesh, 'pivot-gizmo');
    });

    // Store reference for cleanup
    this.tempGizmo = gizmoGroup;
    this.viewer.impl.invalidate(true, true, true);

    // Auto-remove after 1.5 seconds
    this.gizmoTimeout = setTimeout(() => {
      this._hidePivotGizmo();
    }, 1500);
  }

  /**
   * Removes the temporary pivot gizmo if visible.
   */
  _hidePivotGizmo() {
    if (this.gizmoTimeout) {
      clearTimeout(this.gizmoTimeout);
      this.gizmoTimeout = null;
    }

    if (this.tempGizmo) {
      const group = Array.isArray(this.tempGizmo) ? this.tempGizmo : [this.tempGizmo];

      group.forEach(mesh => {
        if (mesh) {
          this.viewer.overlays.removeMesh(mesh, 'pivot-gizmo');
          if (mesh.geometry) mesh.geometry.dispose();
          if (mesh.material) mesh.material.dispose();
        }
      });

      this.tempGizmo = null;
      this.viewer.impl.invalidate(true, true, true);
    }
  }
}

export default PivotSetterTool;
